import React, { useState, useMemo, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getDpLedgerListApi } from "../../api/fakebackend_helper";
import decrypted from "../../helper/decrpted";
import Pagintion from "../../common/Pagination";

const FinancialYearInput = React.forwardRef(({ value, onClick }, ref) => {
  let displayVal = value;
  if (value) {
    const startYear = parseInt(value, 10);
    if (!isNaN(startYear)) {
      displayVal = `${startYear}-${startYear + 1}`;
    }
  }
  return (
    <input
      ref={ref}
      onClick={onClick}
      value={displayVal || ""}
      readOnly
      className="statements-filter-input"
      style={{ cursor: "pointer" }}
    />
  );
});

const DpLedgerView = ({ clientCode: propClientCode }) => {
  const [selectedDemat, setSelectedDemat] = useState("All");
  const [fYear, setFYear] = useState(new Date(2026, 3, 1));
  const [fromDate, setFromDate] = useState(new Date(2026, 3, 1));
  const [toDate, setToDate] = useState(new Date(2027, 2, 31));
  const [cashBalance, setCashBalance] = useState(252.89);
  const [loading, setLoading] = useState(false);

  const decryptedResult = useMemo(() => {
    try {
      const userData = JSON.parse(sessionStorage.getItem("userData") || localStorage.getItem("userData") || "null");
      return decrypted(userData);
    } catch (e) {
      return null;
    }
  }, []);

  const clientCode = useMemo(() => {
    return (propClientCode || "").trim();
  }, [propClientCode]);

  const dematOptions = useMemo(() => {
    const options = [{ label: "All Demat Accounts", value: "All" }];
    if (clientCode) {
      options.push({ label: `IN300214-${clientCode}`, value: `IN300214${clientCode}` });
    }
    return options;
  }, [clientCode]);


  const finYearOptions = useMemo(() => {
    const years = [];
    const currentYear = new Date().getFullYear();
    const endYear = Math.max(currentYear + 2, 2028);
    const startYear = 2015;
    for (let yr = endYear; yr >= startYear; yr--) {
      years.push({
        value: `${yr}`,
        label: `${yr}-${yr + 1}`,
      });
    }
    return years;
  }, []);

  const [dpLedgerList, setDpLedgerList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const handleFinancialYearChange = (date) => {
    if (date) {
      setFYear(date);
      const selectedYear = date.getFullYear();
      setFromDate(new Date(selectedYear, 3, 1));
      setToDate(new Date(selectedYear + 1, 2, 31));
    }
  };

  const fetchDpLedgerData = useCallback(async () => {
    setLoading(true);
    try {
      const boid = selectedDemat !== "All" ? selectedDemat.replace(/[^0-9A-Za-z]/g, "") : "ALL";

      if (!clientCode) {
        setDpLedgerList([]);
        setLoading(false);
        return;
      }

      const res = await getDpLedgerListApi({
        clientCode: clientCode,
        ldCode: clientCode,
        fromDate: dayjs(fromDate).format("YYYY-MM-DD"),
        toDate: dayjs(toDate).format("YYYY-MM-DD"),
        cclientboid: boid,
      });

      const resData =
        res?.data?.data ||
        res?.data?.list ||
        res?.data?.result ||
        (Array.isArray(res?.data) ? res?.data : null);

      if (resData && Array.isArray(resData) && resData.length > 0) {
        let runningBal = 0;
        const mapped = resData.map((item, idx) => {
          const debit = parseFloat(item.nledgerdebitamt ?? item.d_amt ?? item.debit ?? item.dr_amount ?? 0);
          const credit = parseFloat(item.nledgercreditamt ?? item.c_amt ?? item.credit ?? item.cr_amount ?? 0);

          if (item.c_bal !== undefined && item.c_bal !== null) {
            runningBal = parseFloat(item.c_bal);
          } else if (item.closingBalance !== undefined && item.closingBalance !== null) {
            runningBal = parseFloat(item.closingBalance);
          } else {
            if (item.cledgerdebitcredit === "D") {
              runningBal -= debit;
            } else {
              runningBal += credit;
            }
          }

          return {
            id: item.id || idx + 1,
            dpClientId: item.cclientboid || item.dp_client_id || item.boid || `IN300214-${clientCode}`,
            ledgerDate: item.dledgerdate || item.ld_date || item.ledger_date || item.date || "",
            voucherNumber: item.cledgervouchernumber || item.voucher_no || item.voucherNumber || "-",
            dpBillingDescription: item.cledgernarration || item.dp_desc || item.narration || item.description || "-",
            debit: debit,
            credit: credit,
            closingBalance: runningBal,
          };
        });
        setDpLedgerList(mapped);
        if (mapped.length > 0) {
          setCashBalance(mapped[mapped.length - 1].closingBalance);
        }
      } else {
        setDpLedgerList([]);
        setCashBalance(0);
      }
    } catch (err) {
      console.error("Error fetching DP Ledger data:", err);
      setDpLedgerList([]);
      setCashBalance(0);
    } finally {
      setLoading(false);
    }
  }, [selectedDemat, fromDate, toDate, clientCode]);

  useEffect(() => {
    fetchDpLedgerData();
  }, [fetchDpLedgerData]);


  useEffect(() => {
    let result = dpLedgerList;
    if (selectedDemat !== "All") {
      const cleanSelected = selectedDemat.replace(/[^0-9A-Za-z]/g, "");
      result = result.filter((item) =>
        item.dpClientId.replace(/[^0-9A-Za-z]/g, "").includes(cleanSelected)
      );
    }
    setFilteredData(result);
    setCurrentPage(1);
  }, [selectedDemat, dpLedgerList]);

  const handleSearch = () => {
    fetchDpLedgerData();
  };

  const downloadReport = () => {
    if (filteredData.length === 0) return;
    const csvHeader = [
      "DP ID / Client ID",
      "Ledger Date",
      "Voucher Number",
      "DP Billing Description",
      "Debit",
      "Credit",
      "Closing Balance",
    ];
    const csvRows = filteredData.map((item) => [
      item.dpClientId,
      item.ledgerDate ? dayjs(item.ledgerDate).format("DD MMM YYYY") : "",
      item.voucherNumber,
      `"${item.dpBillingDescription}"`,
      item.debit.toFixed(2),
      item.credit.toFixed(2),
      item.closingBalance.toFixed(2),
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [csvHeader.join(","), ...csvRows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `DP_Ledger_${dayjs().format("YYYY-MM-DD")}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;

  return (
    <div>
      <div className="statements-sub-header">
        <div></div>
        <div className="statements-summary-box">
          <div className="statements-cash-balance">
            Cash Balance : ₹{cashBalance.toFixed(2)}
          </div>
          <button className="btn-download-report" onClick={downloadReport}>
            📥 Download Report
          </button>
        </div>
      </div>

      <div className="statements-filter-bar">
        <div className="statements-filter-group">
          <label className="statements-filter-label">Select Demat A/C No.</label>
          <select
            className="statements-filter-input"
            value={selectedDemat}
            onChange={(e) => setSelectedDemat(e.target.value)}
          >
            {dematOptions.map((opt, idx) => (
              <option key={idx} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="statements-filter-group">
          <label className="statements-filter-label">Select Financial Year</label>
          <DatePicker
            selected={fYear}
            onChange={handleFinancialYearChange}
            showYearPicker
            dateFormat="yyyy"
            customInput={<FinancialYearInput />}
          />
        </div>

        <div className="statements-filter-group">
          <label className="statements-filter-label">From</label>
          <DatePicker
            selected={fromDate}
            onChange={(date) => setFromDate(date)}
            dateFormat="dd MMM, yyyy"
            className="statements-filter-input"
          />
        </div>

        <div className="statements-filter-group">
          <label className="statements-filter-label">To</label>
          <DatePicker
            selected={toDate}
            onChange={(date) => setToDate(date)}
            dateFormat="dd MMM, yyyy"
            className="statements-filter-input"
          />
        </div>

        <button className="btn-statements-search" onClick={handleSearch}>
          Search
        </button>
      </div>

      <div className="statements-table-responsive">
        {loading ? (
          <div className="statements-loading-spinner">Loading DP Ledger...</div>
        ) : (
          <table className="statements-data-table">
            <thead>
              <tr>
                <th>DP ID / Client ID</th>
                <th>Ledger Date</th>
                <th>Voucher Number</th>
                <th>DP Billing Description</th>
                <th className="text-right">Debit (₹)</th>
                <th className="text-right">Credit (₹)</th>
                <th className="text-right">Closing Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                paginatedData.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className="dp-id-badge">{row.dpClientId}</span>
                    </td>
                    <td>
                      {row.ledgerDate
                        ? dayjs(row.ledgerDate).format("DD MMM YYYY")
                        : "-"}
                    </td>
                    <td>{row.voucherNumber}</td>
                    <td className="statements-stock-cell">{row.dpBillingDescription}</td>
                    <td className="text-right">{row.debit.toFixed(2)}</td>
                    <td className="text-right">{row.credit.toFixed(2)}</td>
                    <td className="text-right val-positive">
                      {row.closingBalance.toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="statements-empty-state">
                    No ledger records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {filteredData.length > 0 && (
        <div className="mt-3">
          <Pagintion
            currentPage={currentPage}
            totalPages={totalPages}
            paginate={(pageNumber) => setCurrentPage(pageNumber)}
            listLength={filteredData.length}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default DpLedgerView;
